import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { DriverSearchField } from '../reports/DriverSearchField'
import { fetchMbmtAlerts, type MbmtAlertItem } from './useMbmtApi'

/** MBMT's own live Alerts panel (.claude/work/SPEC.md addendum, AC-9) -- the
 *  left-nav 'Alerts' chip's content. GET /api/mbmt/reports/alerts (an existing
 *  endpoint) could NOT be reused here: it only ever returns kind x category x
 *  route rollups, never a flat per-alert list -- see that function's own docstring.
 *  This calls the new GET /api/mbmt/alerts instead.
 *
 *  refreshTick, NOT its own poll timer (adversarial review, MED) -- a bare
 *  setInterval/pollWhileVisible loop here would be a SECOND independent poll on
 *  this page, on top of BusOpsShell's own 8s heal-poll. CLAUDE.md's live-data
 *  section: "Every page gets at most one heal-poll... A new polling loop is not an
 *  acceptable default." refreshTick is a plain counter BusOpsShell bumps once per
 *  its own refresh() cycle; this effect re-fetches whenever that advances, or
 *  whenever a filter changes -- riding the shell's one shared clock rather than
 *  keeping its own.
 *
 *  No VehicleSearchField reuse for the vehicle filter -- that component is
 *  hardwired to /api/reports/soh_coverage (Ultratech truck plates), wrong roster
 *  for MBMT buses, and has no override param. A plain exact-match text input is
 *  what the backend's own vehicle_id filter already is (serving/mbmt.py's
 *  _mbmt_alert_items), so this does not fabricate a roster that does not exist. */

const SEVERITY_BORDER: Record<string, string> = {
  CRITICAL: 'border-l-[#9F1239]',
  WARN: 'border-l-[#B45309]',
  INFO: 'border-l-[#0369A1]',
}

const SEVERITY_BADGE: Record<string, { label: string; bg: string; ink: string }> = {
  CRITICAL: { label: 'HIGH', bg: 'rgba(244,63,94,0.14)', ink: '#9F1239' },
  WARN: { label: 'MED', bg: 'rgba(245,158,11,0.14)', ink: '#B45309' },
  INFO: { label: 'LOW', bg: 'rgba(14,165,233,0.14)', ink: '#0369A1' },
}

const IST_CLOCK = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false,
})

const inputCls =
  'rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] text-slate-700 ' +
  'focus:border-amber-400 focus:outline-none'

export function MbmtAlertsFeed({ refreshTick }: { refreshTick: number }): JSX.Element {
  const [alerts, setAlerts] = useState<MbmtAlertItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [vehicleId, setVehicleId] = useState('')
  const [driverName, setDriverName] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  useEffect(() => {
    let alive = true
    fetchMbmtAlerts(dateFrom, dateTo, vehicleId, driverName)
      .then(({ alerts: rows }) => {
        if (!alive) return
        setAlerts(rows)
        setError(null)
      })
      .catch((e: Error) => { if (alive) setError(e.message) })
      .finally(() => { if (alive) setLoaded(true) })
    return () => { alive = false }
  }, [refreshTick, dateFrom, dateTo, vehicleId, driverName])

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
          vehicle
          <input
            type="search"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            placeholder="MH04LY…"
            className={`${inputCls} w-[160px] font-mono normal-case`}
          />
        </label>
        <DriverSearchField
          value={driverName}
          onChange={setDriverName}
          rosterPath="/api/mbmt/driver-roster"
        />
        <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
          from
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-1 text-[10.5px] font-medium uppercase tracking-wide text-slate-500">
          to
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={inputCls}
          />
        </label>
      </div>

      {!loaded ? (
        <p className="p-4 text-[12px] text-[#475569]">Loading alerts…</p>
      ) : error ? (
        <p className="p-4 text-[12px] text-[#9F1239]">{error}</p>
      ) : alerts.length === 0 ? (
        <p className="p-4 text-[12px] text-[#475569]">No alerts match these filters.</p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto rounded-[3px] border border-[rgba(15,23,42,0.14)] bg-[rgba(15,23,42,0.03)] p-3">
          {alerts.map((a, i) => {
            const badge = SEVERITY_BADGE[a.severity] ?? SEVERITY_BADGE.INFO!
            return (
              <div
                key={`${a.vehicle_id}:${a.category}:${a.ts}:${i}`}
                className={clsx(
                  'flex items-start gap-2.5 rounded-[3px] border border-[rgba(15,23,42,0.14)] border-l-[3px] bg-white px-3 py-2.5',
                  SEVERITY_BORDER[a.severity] ?? SEVERITY_BORDER.INFO,
                )}
              >
                <div className="flex-1">
                  <div className="text-[12px] font-medium text-[#0F172A]">
                    {a.category} — {a.vehicle_id}
                    {a.driver_name ? ` · ${a.driver_name}` : ''}
                  </div>
                  {a.message ? <div className="mt-0.5 text-[11px] text-[#475569]">{a.message}</div> : null}
                  <div className="mt-0.5 font-mono text-[10px] text-[#94A3B8]">
                    {IST_CLOCK.format(new Date(a.ts))} IST
                  </div>
                </div>
                <span
                  className="shrink-0 rounded-[2px] px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.08em]"
                  style={{ background: badge.bg, color: badge.ink }}
                >
                  {badge.label}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
